import React from 'react';
import { Utensils, Droplets, Battery, Heart, Clock } from 'lucide-react';

const NutritionGuide: React.FC = () => {
  const sections = [
    {
      icon: <Utensils size={20} className="text-blue-400" />,
      title: 'Training Nutrition',
      content: 'Fuel your body with a balance of carbohydrates, protein, and healthy fats. Carbs are your primary energy source for endurance training.',
    },
    {
      icon: <Droplets size={20} className="text-cyan-400" />,
      title: 'Hydration',
      content: 'Drink 500ml of water 2 hours before training. During sessions over 60 minutes, aim for 500-750ml per hour. Adjust based on sweat rate.',
    },
    {
      icon: <Battery size={20} className="text-orange-400" />,
      title: 'Race Day Fueling',
      content: 'Consume 30-60g of carbohydrates per hour during race pace efforts. Practice your race nutrition in training to avoid surprises.',
    },
    {
      icon: <Heart size={20} className="text-red-400" />,
      title: 'Recovery Nutrition',
      content: 'Eat protein and carbs within 30-60 minutes post-workout. Aim for 20-30g protein and 1g carbs per kg body weight for optimal recovery.',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 flex items-center">
          <Utensils size={28} className="mr-3 text-[#3b82f6]" />
          Nutrition Guide
        </h1>
        <p className="text-muted">Fueling strategies for triathletes</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {sections.map((section, index) => (
          <div key={index} className="card-base p-6">
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-[#0a0e14] rounded-lg">
                {section.icon}
              </div>
              <div>
                <h3 className="font-semibold mb-2">{section.title}</h3>
                <p className="text-sm text-muted">{section.content}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card-base p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Clock size={20} className="mr-2 text-[#3b82f6]" />
          Daily Nutrition Timeline
        </h2>
        <div className="space-y-4">
          <div className="flex">
            <span className="w-24 text-sm text-muted">Morning</span>
            <span className="text-[#e2e8f0]">Light breakfast with carbs and protein (oatmeal with eggs, or toast with peanut butter)</span>
          </div>
          <div className="flex">
            <span className="w-24 text-sm text-muted">Pre-Training</span>
            <span className="text-[#e2e8f0]">Easy digestible carbs 1-2 hours before (banana, toast, sports drink)</span>
          </div>
          <div className="flex">
            <span className="w-24 text-sm text-muted">During</span>
            <span className="text-[#e2e8f0]">Hydration + electrolytes. Gels/chews for sessions over 75 minutes.</span>
          </div>
          <div className="flex">
            <span className="w-24 text-sm text-muted">Post-Training</span>
            <span className="text-[#e2e8f0]">Recovery shake or meal within 60 minutes (protein + carbs)</span>
          </div>
          <div className="flex">
            <span className="w-24 text-sm text-muted">Evening</span>
            <span className="text-[#e2e8f0]">Balanced dinner with protein, veggies, and moderate carbs</span>
          </div>
        </div>
      </div>

      <div className="card-base p-6">
        <h2 className="text-xl font-semibold mb-4">Common Mistakes to Avoid</h2>
        <ul className="space-y-3 text-muted">
          <li className="flex items-center space-x-2">
            <span className="text-red-400">✗</span>
            <span>Eating too much fiber before hard sessions</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="text-red-400">✗</span>
            <span>Trying new foods on race day</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="text-red-400">✗</span>
            <span>Waiting until you're thirsty to drink</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="text-red-400">✗</span>
            <span>Skipping post-workout nutrition</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="text-red-400">✗</span>
            <span>Overhydrating (can be as dangerous as dehydration)</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default NutritionGuide;
